import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { materialsApi, type Material, type MaterialInput, type MaterialType, type MaterialScope } from '@/api/materials';
import { coursesApi } from '@/api/courses';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MBlock } from '@/components/mobile/MobileKit';
import { cn } from '@/lib/utils';

/**
 * Extracts a YouTube video id from the common URL shapes so we can render the
 * canonical thumbnail in the card preview (the server does the same derivation
 * server-side when cover_url is empty for videos).
 */
export function youtubeId(url?: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{11})/,
    /[?&]v=([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function coverFor(m: Material): string | undefined {
  if (m.cover_url) return m.cover_url;
  if (m.type === 'video') {
    const id = youtubeId(m.youtube_url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return undefined;
}

export function AdminMaterialsPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);

  const list = useQuery({ queryKey: ['admin', 'materials'], queryFn: () => materialsApi.listAdmin() });
  const marathonsList = useQuery({
    queryKey: ['admin', 'materials', 'marathons'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
  });
  const marathonMap = new Map((marathonsList.data?.items ?? []).map((m) => [m.id, m.title]));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'materials'] });
  const remove = useMutation({
    mutationFn: (id: string) => materialsApi.removeAdmin(id),
    onSuccess: invalidate,
  });

  const sorted = [...(list.data ?? [])].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );

  return (
    <>
      <PageHeader
        title={t('materialsAdmin.title')}
        subtitle={t('materialsAdmin.subtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('materialsAdmin.createBtn')}
          </Button>
        }
      />

      {remove.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{humanizeError(remove.error)}</Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !sorted.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('materialsAdmin.empty')}{' '}
          <button type="button" onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('materialsAdmin.createFirst')}
          </button>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          {sorted.map((m) => (
            <li key={m.id}>
              <MaterialCard
                m={m}
                marathonLabel={m.marathon_id ? marathonMap.get(m.marathon_id) : undefined}
                onEdit={() => setEditing(m)}
                onDelete={async () => {
                  if (await ask({ message: t('materialsAdmin.deleteConfirm', { title: m.title }), danger: true })) {
                    remove.mutate(m.id);
                  }
                }}
              />
            </li>
          ))}
        </ul>
      )}

      {creatingOpen && <MaterialModal onClose={() => setCreatingOpen(false)} onSaved={invalidate} />}
      {editing && (
        <MaterialModal material={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}
    </>
  );
}

export function MaterialCard({
  m,
  marathonLabel,
  scopeLabelOverride,
  onEdit,
  onDelete,
}: {
  m: Material;
  marathonLabel?: string;
  scopeLabelOverride?: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const cover = coverFor(m);
  const scopeLabel =
    scopeLabelOverride ??
    (m.scope === 'global'
      ? t('materialsAdmin.scopeGlobal')
      : m.scope === 'marathon'
        ? marathonLabel ?? t('materialsAdmin.scopeMarathon')
        : t('materialsAdmin.scopeCurator'));

  return (
    <Card className="p-0 overflow-hidden">
      <div
        className="relative flex h-44 items-center justify-center bg-surface-container-low bg-cover bg-center text-on-surface-variant"
        style={cover ? { backgroundImage: `url(${cover})` } : undefined}
      >
        {!cover && (
          <span className="material-symbols-outlined text-4xl opacity-60" aria-hidden>
            {m.type === 'video' ? 'movie' : 'article'}
          </span>
        )}
        <span className="absolute top-2 left-2 flex gap-1">
          <Pill variant="primary">
            <span className="material-symbols-outlined text-sm">
              {m.type === 'video' ? 'play_circle' : 'article'}
            </span>
            {m.type === 'video' ? t('materialsAdmin.typeVideo') : t('materialsAdmin.typeArticle')}
          </Pill>
        </span>
        <span className="absolute top-2 right-2 flex gap-1">
          <Pill variant={m.is_active ? 'success' : 'neutral'}>
            {m.is_active ? t('materialsAdmin.statusActive') : t('materialsAdmin.statusHidden')}
          </Pill>
          <Pill variant="neutral">#{m.display_order ?? 0}</Pill>
        </span>
      </div>
      <div className="p-4">
        <CardTitle>{m.title}</CardTitle>
        <p className="mt-1 text-xs text-on-surface-variant">{scopeLabel}</p>
        {m.description && (
          <p className="mt-1 line-clamp-2 text-xs text-on-surface-variant">{m.description}</p>
        )}
        <div className="mt-3 flex gap-2">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <span className="material-symbols-outlined">edit</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <span className="material-symbols-outlined">delete</span>
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Материалы строками — телефон сотрудника (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §4.17): обложка 56 px, название
 * в две строки, «Видео / Статья · активен · #порядок · кому». Нажатие — правка; «⋯» — лист «Изменить / Удалить».
 * Подтверждение удаления — у вызывающего, то же, что на компьютере.
 */
export function MaterialRows({
  items,
  scopeLabelOf,
  onEdit,
  onDelete,
}: {
  items: Material[];
  scopeLabelOf: (m: Material) => string;
  onEdit: (m: Material) => void;
  onDelete: (m: Material) => void;
}) {
  const { t } = useTranslation();
  const [sheetFor, setSheetFor] = useState<Material | null>(null);
  return (
    <>
      <MBlock>
        {items.map((m, i) => {
          const cover = coverFor(m);
          return (
            <div
              key={m.id}
              data-material-row
              className={cn('flex items-center gap-2 py-3', i < items.length - 1 && 'border-b border-outline')}
            >
              <button type="button" onClick={() => onEdit(m)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                <span
                  className="grid h-14 w-14 flex-none place-items-center overflow-hidden rounded-2xl bg-surface-container-high bg-cover bg-center text-on-surface-variant"
                  style={cover ? { backgroundImage: `url(${cover})` } : undefined}
                >
                  {!cover && (
                    <span className="material-symbols-outlined text-[22px]" aria-hidden>
                      {m.type === 'video' ? 'movie' : 'article'}
                    </span>
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[14px] font-semibold leading-[1.3] text-on-surface">{m.title}</span>
                  <span className="block truncate text-[11.5px] text-on-surface-variant">
                    {[
                      m.type === 'video' ? t('materialsAdmin.typeVideo') : t('materialsAdmin.typeArticle'),
                      m.is_active ? t('materialsAdmin.statusActive') : t('materialsAdmin.statusHidden'),
                      `#${m.display_order ?? 0}`,
                      scopeLabelOf(m),
                    ].join(' · ')}
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSheetFor(m)}
                aria-label={t('nav.more')}
                className="grid h-11 w-11 flex-none place-items-center rounded-full text-on-surface-variant"
              >
                <span className="material-symbols-outlined text-[20px]">more_horiz</span>
              </button>
            </div>
          );
        })}
      </MBlock>
      <ActionSheet
        open={!!sheetFor}
        onClose={() => setSheetFor(null)}
        title={sheetFor?.title}
        actions={
          sheetFor
            ? [
                { label: t('common.edit'), icon: 'edit', onSelect: () => onEdit(sheetFor) },
                { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => onDelete(sheetFor) },
              ]
            : []
        }
      />
    </>
  );
}

/**
 * Create/edit modal. When `forceCuratorScope` is set (curator page) the scope is
 * locked to `curator` and the scope/marathon selectors are hidden.
 */
export function MaterialModal({
  material,
  forceCuratorScope = false,
  onClose,
  onSaved,
}: {
  material?: Material;
  forceCuratorScope?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(material);
  const [data, setData] = useState<MaterialInput>(
    material ?? {
      type: 'video',
      title: '',
      description: '',
      youtube_url: '',
      body_html: '',
      cover_url: '',
      scope: forceCuratorScope ? 'curator' : 'global',
      points: 0,
      is_active: true,
      display_order: 0,
    },
  );

  const marathonsList = useQuery({
    queryKey: ['admin', 'materials', 'modal-marathons'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
    enabled: !forceCuratorScope,
  });

  const save = useMutation({
    mutationFn: () => {
      // Build a clean payload; the server treats PUT as a partial update.
      const payload: MaterialInput = { ...data };
      if (forceCuratorScope) {
        // Server forces scope=curator + curator_id; don't send client-controlled scope.
        delete payload.scope;
        delete payload.marathon_id;
        delete payload.curator_id;
      } else if (payload.scope !== 'marathon') {
        payload.marathon_id = null;
      }
      if (isEdit) {
        return forceCuratorScope
          ? materialsApi.updateCurator(material!.id, payload)
          : materialsApi.updateAdmin(material!.id, payload);
      }
      return forceCuratorScope
        ? materialsApi.createCurator(payload)
        : materialsApi.createAdmin(payload);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const isVideo = data.type === 'video';
  const needsMarathon = !forceCuratorScope && data.scope === 'marathon';
  const canSave =
    !!data.title?.trim() &&
    (isVideo ? !!data.youtube_url?.trim() : !!data.body_html?.trim()) &&
    (!needsMarathon || !!data.marathon_id);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isEdit ? t('materialsAdmin.editTitle') : t('materialsAdmin.newTitle')}
      phoneFull
    >
      <div className="space-y-3">
        <Select
          label={t('materialsAdmin.fieldType')}
          value={data.type}
          onChange={(e) => setData({ ...data, type: e.target.value as MaterialType })}
          options={[
            { value: 'video', label: t('materialsAdmin.typeVideo') },
            { value: 'article', label: t('materialsAdmin.typeArticle') },
          ]}
        />

        <Input
          label={t('materialsAdmin.fieldTitle')}
          value={data.title ?? ''}
          onChange={(e) => setData({ ...data, title: e.target.value })}
        />

        <Input
          label={t('materialsAdmin.fieldDescription')}
          value={data.description ?? ''}
          onChange={(e) => setData({ ...data, description: e.target.value })}
        />

        {isVideo ? (
          <Input
            label={t('materialsAdmin.fieldYoutubeUrl')}
            hint={t('materialsAdmin.fieldYoutubeUrlHint')}
            value={data.youtube_url ?? ''}
            onChange={(e) => setData({ ...data, youtube_url: e.target.value })}
          />
        ) : (
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('materialsAdmin.fieldBody')}
            </span>
            <textarea
              className="min-h-[180px] w-full rounded-button bg-surface-container-lowest p-4 text-on-surface shadow-soft transition focus:shadow-ambient focus:outline-none"
              placeholder={t('materialsAdmin.fieldBodyHint')}
              value={data.body_html ?? ''}
              onChange={(e) => setData({ ...data, body_html: e.target.value })}
            />
          </label>
        )}

        <Input
          label={t('materialsAdmin.fieldCoverUrl')}
          hint={isVideo ? t('materialsAdmin.fieldCoverUrlVideoHint') : undefined}
          value={data.cover_url ?? ''}
          onChange={(e) => setData({ ...data, cover_url: e.target.value })}
        />

        {!forceCuratorScope && (
          <>
            <Select
              label={t('materialsAdmin.fieldScope')}
              value={data.scope}
              onChange={(e) => setData({ ...data, scope: e.target.value as MaterialScope })}
              options={[
                { value: 'global', label: t('materialsAdmin.scopeGlobal') },
                { value: 'marathon', label: t('materialsAdmin.scopeMarathon') },
              ]}
            />
            {needsMarathon && (
              <Select
                label={t('materialsAdmin.fieldMarathon')}
                value={data.marathon_id ?? ''}
                onChange={(e) => setData({ ...data, marathon_id: e.target.value || null })}
                options={[
                  { value: '', label: t('materialsAdmin.selectMarathon') },
                  ...(marathonsList.data?.items?.map((m) => ({ value: m.id, label: m.title })) ?? []),
                ]}
              />
            )}
          </>
        )}

        {/*
          ⚠️ ПОЛЕ «ОЧКИ ЗА ПРОСМОТР» УБРАНО 10.09.2026 (разбаловка).
          Начисления за просмотр материала больше нет нигде: очко на платформе
          теперь значит «оценка за урок», и ничего кроме. Поле оставили бы —
          администратор ставил бы 50, а ученик не получал ничего.
          Колонка materials.points в базе осталась: это единственная запись
          прежнего замысла, и удалять её задним числом незачем.
        */}

        <Input
          type="number"
          label={t('materialsAdmin.fieldDisplayOrder')}
          value={data.display_order ?? 0}
          onChange={(e) => setData({ ...data, display_order: Number(e.target.value) })}
        />

        <Select
          label={t('materialsAdmin.fieldStatus')}
          value={data.is_active ? 'on' : 'off'}
          onChange={(e) => setData({ ...data, is_active: e.target.value === 'on' })}
          options={[
            { value: 'on', label: t('materialsAdmin.statusActive') },
            { value: 'off', label: t('materialsAdmin.statusHidden') },
          ]}
        />

        {save.error && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        {!canSave && (
          <p className="text-xs text-on-surface-variant">
            {needsMarathon
              ? t('materialsAdmin.validationHintMarathon')
              : isVideo
                ? t('materialsAdmin.validationHintVideo')
                : t('materialsAdmin.validationHintArticle')}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
            {isEdit ? t('common.save') : t('materialsAdmin.publishBtn')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
